<script setup lang="ts">
  import { computed, ref, watch } from "vue";
  import { CircleAlert, LoaderCircle } from "lucide-vue-next";
  import AiStar from "@/components/episto-ui/ai/AiStar.vue";
  import { Input } from "@/components/ui/input";
  import { Button } from "@/components/ui/button";

  const target = defineModel<string>("target", { default: "" });
  const topic = defineModel<string>("topic", { default: "" });

  const props = defineProps<{
    loading?: boolean;
    active?: boolean;
    error?: boolean;
    resultCount?: number;
    appliedTarget?: string;
    appliedTopic?: string;
  }>();

  const emit = defineEmits<{ search: []; clear: []; cancel: []; retry: [] }>();

  const editing = ref(false);

  const hasTarget = computed(() => !!target.value?.trim());
  const showPanel = computed(() => !props.active || editing.value);
  const countLabel = computed(() => `${props.resultCount ?? 0} ${props.resultCount === 1 ? "study" : "studies"}`);

  const submit = () => {
    if (!hasTarget.value || props.loading) return;
    editing.value = false;
    emit("search");
  };

  const startEditing = () => (editing.value = true);

  const cancelEditing = () => {
    target.value = props.appliedTarget ?? "";
    topic.value = props.appliedTopic ?? "";
    editing.value = false;
  };

  watch(
    () => props.active,
    (isActive) => {
      if (!isActive) editing.value = false;
    },
  );
</script>

<template>
  <div
    v-if="showPanel"
    class="flex flex-col gap-3.5 rounded-xl border border-secondary bg-muted p-5"
    @keydown.esc="editing && cancelEditing()">
    <div class="flex flex-col gap-1">
      <p class="flex items-center gap-2 heading-title">
        <AiStar class="size-4" />
        {{ editing ? "Edit search" : "Find comparable studies" }}
      </p>
      <p class="text-13 text-muted-foreground">
        Describe the target and, optionally, the topic. Results are ranked by relevance.
      </p>
    </div>

    <div class="grid grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)_auto] items-end gap-2">
      <label class="flex flex-col gap-1.5">
        <span class="text-13 font-medium text-foreground-secondary">Target</span>
        <Input
          v-model="target"
          placeholder="e.g. Parents 25–45, France"
          class="h-10 bg-background"
          @keydown.enter="submit" />
      </label>
      <label class="flex flex-col gap-1.5">
        <span class="text-13 font-medium text-foreground-secondary">
          Topic
          <span class="font-normal text-foreground-subtle">optional</span>
        </span>
        <Input
          v-model="topic"
          placeholder="e.g. Petfood"
          class="h-10 bg-background"
          @keydown.enter="submit" />
      </label>
      <Button
        v-if="!editing"
        class="h-10 px-5"
        :disabled="!hasTarget || props.loading"
        @click="submit">
        Search
      </Button>
    </div>

    <div
      v-if="editing"
      class="flex justify-end gap-2">
      <Button
        variant="ghost"
        @click="cancelEditing">
        Cancel
      </Button>
      <Button
        :disabled="!hasTarget || props.loading"
        @click="submit">
        Update search
      </Button>
    </div>
  </div>

  <div
    v-else
    class="flex items-center gap-2.5 rounded-lg border px-3.5 py-2.5 text-13"
    :class="props.error ? 'border-status-error-border bg-status-error-soft' : 'border-secondary bg-muted'">
    <template v-if="props.error">
      <CircleAlert class="size-4 shrink-0 text-status-error-strong" />
      <span class="min-w-0 flex-1">
        <span class="text-foreground">AI search is unavailable right now.</span>
        <span class="text-muted-foreground"> You can still filter by keyword.</span>
      </span>
      <Button
        variant="outline"
        size="sm"
        @click="emit('retry')">
        Retry
      </Button>
      <Button
        variant="ghost"
        size="sm"
        class="text-primary"
        @click="startEditing">
        Edit
      </Button>
    </template>

    <template v-else-if="props.loading">
      <LoaderCircle class="size-4 shrink-0 animate-spin text-primary" />
      <span class="min-w-0 flex-1 truncate">
        <span class="font-medium text-primary">Searching comparable studies…</span>
        <span class="text-foreground-subtle"> {{ props.appliedTarget }}</span>
        <span v-if="props.appliedTopic" class="text-foreground-subtle"> · {{ props.appliedTopic }}</span>
      </span>
      <Button
        variant="ghost"
        size="sm"
        class="text-muted-foreground"
        @click="emit('cancel')">
        Cancel
      </Button>
    </template>

    <template v-else>
      <AiStar class="size-4 shrink-0 text-primary" />
      <span class="min-w-0 flex-1 truncate">
        <span class="font-medium text-primary">{{ props.appliedTarget }}</span>
        <template v-if="props.appliedTopic">
          <span class="text-border-strong"> · </span>
          <span class="text-foreground">{{ props.appliedTopic }}</span>
        </template>
        <span class="text-border-strong"> · </span>
        <span class="text-muted-foreground">{{ countLabel }}</span>
      </span>
      <Button
        variant="ghost"
        size="sm"
        class="text-primary"
        @click="startEditing">
        Edit
      </Button>
      <Button
        variant="ghost"
        size="sm"
        class="text-muted-foreground"
        @click="emit('clear')">
        Clear
      </Button>
    </template>
  </div>
</template>
